/**
 * Tiny synthesizer for sound effects and a gentle generative soundtrack. No
 * audio files: everything is made with oscillators so the game stays light.
 */
export type Sfx =
  | 'click'
  | 'plant'
  | 'water'
  | 'harvest'
  | 'golden'
  | 'coin'
  | 'buy'
  | 'levelUp'
  | 'discover'
  | 'error'
  | 'bloom'
  | 'dig'
  | 'unlock'
  | 'order'
  | 'pop'
  | 'chat';

const NOTE = (semitonesFromA4: number) => 440 * Math.pow(2, semitonesFromA4 / 12);
// C major pentatonic, two octaves starting at C5.
const PENTATONIC = [3, 5, 7, 10, 12, 15, 17, 19, 22, 24].map(NOTE);
// Cmaj7 · Am7 · Fmaj7 · G6 (as semitones from A4).
const CHORDS = [
  [-21, -9, -5, -2],
  [-24, -12, -9, -5],
  [-28, -9, -5, -1],
  [-26, -7, -3, 0],
];

export class AudioEngine {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private sfxBus: GainNode | null = null;
  private musicBus: GainNode | null = null;
  private echo: DelayNode | null = null;
  private noiseBuffer: AudioBuffer | null = null;
  private musicTimer: number | null = null;
  private nextBeatTime = 0;
  private beat = 0;
  private lastMelody = 4;
  private sfxVolume = 0.7;
  private musicVolume = 0.5;
  private muted = false;
  private night = false;

  /** Must be called from a user gesture (browser autoplay policy). */
  unlock(): void {
    if (!this.ctx) {
      const Ctor =
        window.AudioContext ??
        (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!Ctor) return;
      const ctx = new Ctor();
      this.ctx = ctx;
      this.master = ctx.createGain();
      this.master.connect(ctx.destination);
      this.sfxBus = ctx.createGain();
      this.sfxBus.connect(this.master);
      this.musicBus = ctx.createGain();
      // A soft feedback echo gives the music a dreamy, spacious feel.
      this.echo = ctx.createDelay(1);
      this.echo.delayTime.value = 0.36;
      const feedback = ctx.createGain();
      feedback.gain.value = 0.32;
      const echoFilter = ctx.createBiquadFilter();
      echoFilter.type = 'lowpass';
      echoFilter.frequency.value = 2200;
      this.musicBus.connect(this.master);
      this.musicBus.connect(this.echo);
      this.echo.connect(echoFilter).connect(feedback).connect(this.echo);
      echoFilter.connect(this.master);

      const length = ctx.sampleRate;
      this.noiseBuffer = ctx.createBuffer(1, length, ctx.sampleRate);
      const data = this.noiseBuffer.getChannelData(0);
      for (let i = 0; i < length; i++) data[i] = Math.random() * 2 - 1;
      this.applyVolumes();
    }
    if (this.ctx.state === 'suspended') void this.ctx.resume();
  }

  setVolumes(music: number, sfx: number, muted: boolean): void {
    this.musicVolume = music;
    this.sfxVolume = sfx;
    this.muted = muted;
    this.applyVolumes();
  }

  setNight(night: boolean): void {
    this.night = night;
  }

  private applyVolumes(): void {
    if (!this.ctx || !this.master || !this.sfxBus || !this.musicBus) return;
    const t = this.ctx.currentTime;
    this.master.gain.setTargetAtTime(this.muted ? 0 : 1, t, 0.05);
    this.sfxBus.gain.setTargetAtTime(this.sfxVolume * 0.6, t, 0.05);
    this.musicBus.gain.setTargetAtTime(this.musicVolume * 0.22, t, 0.2);
  }

  // ---------------------------------------------------------------------------
  // Building blocks
  // ---------------------------------------------------------------------------

  private tone(
    freq: number,
    start: number,
    duration: number,
    {
      type = 'sine' as OscillatorType,
      gain = 0.3,
      attack = 0.005,
      slideTo,
      bus = this.sfxBus,
    }: {
      type?: OscillatorType;
      gain?: number;
      attack?: number;
      slideTo?: number;
      bus?: GainNode | null;
    } = {},
  ): void {
    if (!this.ctx || !bus) return;
    const osc = this.ctx.createOscillator();
    const env = this.ctx.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(freq, start);
    if (slideTo) osc.frequency.exponentialRampToValueAtTime(slideTo, start + duration);
    env.gain.setValueAtTime(0.0001, start);
    env.gain.exponentialRampToValueAtTime(gain, start + attack);
    env.gain.exponentialRampToValueAtTime(0.0001, start + duration);
    osc.connect(env).connect(bus);
    osc.start(start);
    osc.stop(start + duration + 0.05);
  }

  private noise(
    start: number,
    duration: number,
    frequency: number,
    gain: number,
    type: BiquadFilterType = 'bandpass',
  ): void {
    if (!this.ctx || !this.noiseBuffer || !this.sfxBus) return;
    const source = this.ctx.createBufferSource();
    source.buffer = this.noiseBuffer;
    const filter = this.ctx.createBiquadFilter();
    filter.type = type;
    filter.frequency.value = frequency;
    filter.Q.value = 0.8;
    const env = this.ctx.createGain();
    env.gain.setValueAtTime(0.0001, start);
    env.gain.exponentialRampToValueAtTime(gain, start + 0.02);
    env.gain.exponentialRampToValueAtTime(0.0001, start + duration);
    source.connect(filter).connect(env).connect(this.sfxBus);
    source.start(start, Math.random() * 0.5);
    source.stop(start + duration + 0.05);
  }

  private arpeggio(
    notes: number[],
    start: number,
    step: number,
    opts: { type?: OscillatorType; gain?: number; length?: number } = {},
  ): void {
    notes.forEach((f, i) =>
      this.tone(f, start + i * step, opts.length ?? 0.3, {
        type: opts.type ?? 'triangle',
        gain: opts.gain ?? 0.2,
      }),
    );
  }

  // ---------------------------------------------------------------------------
  // Sound effects
  // ---------------------------------------------------------------------------

  play(sfx: Sfx, volume = 1): void {
    const ctx = this.ctx;
    if (!ctx || this.muted || this.sfxVolume <= 0) return;
    const t = ctx.currentTime + 0.01;
    const v = Math.max(0.05, Math.min(1, volume));
    switch (sfx) {
      case 'click':
        this.tone(900, t, 0.05, { type: 'triangle', gain: 0.08 * v });
        break;
      case 'pop':
        this.tone(500, t, 0.12, { type: 'sine', gain: 0.18 * v, slideTo: 900 });
        break;
      case 'plant':
        this.noise(t, 0.12, 500, 0.25 * v, 'lowpass');
        this.tone(330, t + 0.03, 0.16, { gain: 0.2 * v, slideTo: 520 });
        break;
      case 'water':
        this.noise(t, 0.45, 1600, 0.18 * v);
        for (let i = 0; i < 4; i++)
          this.tone(1100 + Math.random() * 900, t + 0.06 + i * 0.07, 0.08, {
            gain: 0.07 * v,
            slideTo: 1800,
          });
        break;
      case 'harvest':
        this.arpeggio([NOTE(3), NOTE(7), NOTE(10), NOTE(15)], t, 0.055, { gain: 0.16 * v });
        break;
      case 'golden':
        this.arpeggio([NOTE(10), NOTE(15), NOTE(19), NOTE(22), NOTE(27)], t, 0.06, {
          type: 'sine',
          gain: 0.18 * v,
          length: 0.5,
        });
        break;
      case 'coin':
        this.tone(NOTE(14), t, 0.08, { type: 'square', gain: 0.07 * v });
        this.tone(NOTE(19), t + 0.08, 0.3, { type: 'square', gain: 0.07 * v });
        break;
      case 'buy':
        this.tone(NOTE(7), t, 0.08, { type: 'triangle', gain: 0.15 * v });
        this.tone(NOTE(14), t + 0.07, 0.2, { type: 'triangle', gain: 0.15 * v });
        break;
      case 'levelUp':
        this.arpeggio([NOTE(3), NOTE(7), NOTE(10), NOTE(15), NOTE(19), NOTE(22)], t, 0.09, {
          gain: 0.2 * v,
          length: 0.4,
        });
        [NOTE(3), NOTE(7), NOTE(10)].forEach((f) =>
          this.tone(f, t + 0.6, 0.9, { type: 'triangle', gain: 0.1 * v, attack: 0.05 }),
        );
        break;
      case 'discover':
        for (let i = 0; i < 8; i++)
          this.tone(PENTATONIC[i % PENTATONIC.length]! * 2, t + i * 0.05, 0.4, {
            type: 'sine',
            gain: 0.1 * v,
          });
        break;
      case 'error':
        this.tone(260, t, 0.12, { type: 'triangle', gain: 0.18 * v });
        this.tone(200, t + 0.12, 0.2, { type: 'triangle', gain: 0.18 * v });
        break;
      case 'bloom':
        this.tone(NOTE(19) * (1 + Math.random() * 0.05), t, 0.7, { type: 'sine', gain: 0.06 * v });
        break;
      case 'dig':
        this.noise(t, 0.18, 300, 0.3 * v, 'lowpass');
        this.tone(140, t, 0.15, { gain: 0.2 * v, slideTo: 80 });
        break;
      case 'unlock':
        this.tone(220, t, 0.08, { type: 'square', gain: 0.06 * v });
        this.tone(330, t + 0.1, 0.08, { type: 'square', gain: 0.06 * v });
        this.arpeggio([NOTE(3), NOTE(10), NOTE(15)], t + 0.2, 0.07, { gain: 0.15 * v });
        break;
      case 'order':
        this.arpeggio([NOTE(10), NOTE(7), NOTE(15)], t, 0.1, { type: 'sine', gain: 0.14 * v });
        break;
      case 'chat':
        this.tone(NOTE(15), t, 0.09, { type: 'sine', gain: 0.08 * v, slideTo: NOTE(19) });
        break;
    }
  }

  // ---------------------------------------------------------------------------
  // Music
  // ---------------------------------------------------------------------------

  startMusic(): void {
    if (!this.ctx || this.musicTimer !== null) return;
    this.nextBeatTime = this.ctx.currentTime + 0.2;
    this.musicTimer = window.setInterval(() => this.scheduleMusic(), 100);
  }

  stopMusic(): void {
    if (this.musicTimer !== null) window.clearInterval(this.musicTimer);
    this.musicTimer = null;
  }

  private scheduleMusic(): void {
    const ctx = this.ctx;
    if (!ctx || this.musicVolume <= 0 || this.muted) {
      if (ctx) this.nextBeatTime = ctx.currentTime + 0.2;
      return;
    }
    const tempo = this.night ? 66 : 80;
    const eighth = 60 / tempo / 2;
    while (this.nextBeatTime < ctx.currentTime + 0.3) {
      const t = this.nextBeatTime;
      const step = this.beat % 8;
      const chord = CHORDS[Math.floor(this.beat / 8) % CHORDS.length]!;
      const octave = this.night ? 0.5 : 1;

      if (step === 0) {
        // Soft pad: the whole chord, slow attack and long release.
        for (const semis of chord) {
          this.tone(NOTE(semis) * octave * 2, t, eighth * 8, {
            type: 'triangle',
            gain: 0.05,
            attack: 0.4,
            bus: this.musicBus,
          });
        }
        this.tone(NOTE(chord[0]!) * octave, t, eighth * 4, {
          type: 'sine',
          gain: 0.12,
          attack: 0.02,
          bus: this.musicBus,
        });
      }
      if (step === 4) {
        this.tone(NOTE(chord[0]! + 7) * octave, t, eighth * 4, {
          type: 'sine',
          gain: 0.08,
          attack: 0.02,
          bus: this.musicBus,
        });
      }
      // Melody: a gentle random walk over the pentatonic scale.
      if (Math.random() < (this.night ? 0.3 : 0.45)) {
        this.lastMelody = Math.max(
          0,
          Math.min(PENTATONIC.length - 1, this.lastMelody + Math.floor(Math.random() * 5) - 2),
        );
        const freq = PENTATONIC[this.lastMelody]! * octave;
        this.tone(freq, t, eighth * 2.5, {
          type: this.night ? 'sine' : 'triangle',
          gain: 0.09,
          attack: 0.01,
          bus: this.musicBus,
        });
      }
      this.nextBeatTime += eighth;
      this.beat++;
    }
  }
}
